/* ========================================================================== 
   COMBOBOX & HARDWARE DROPDOWN CONTROLLER (/database/js/combobox.js)
   ========================================================================== */

import { state, clearSelectedComponent } from '../state.js?v=20260819-audit2';
import { parseSpecNum, formatCaseBrandTitle, AIO_COMBOBOX_IDS, syncComboboxInputValues } from '../utils.js?v=20260819-audit2';
import { batchPreScoreCombobox } from './ui-helpers.js?v=20260819-audit2';
import { updateExplorerUiTitle, renderSingleCaseBreadcrumbs } from '../seo.js?v=20260822-audit3';
import { invalidateActiveReport } from '../reports.js?v=20260822-audit3';
import { runtimeNow, debugPerformanceMetric } from './runtime-perf.js?v=20260819-audit2';
import { createHardwareSearchProfile, createHardwareIdentityProfile } from './hardware-search-index.js?v=20260819-audit2';
import { catalogIdOf } from '../catalog-identity.js?v=20260819-audit2';
import { normalizePsuFormFactor } from './hardware-index.js?v=20260819-audit2';

const COMBOBOX_PAGE_SIZE = 50;
const lazyComboboxOptionModels = new Map();
const comboboxRenderState = new Map();

function prepareOptionModel(model, index) {
  const raw = model && typeof model === 'object' ? model : { text: String(model || '') };
  const identity = String(raw.identity || raw.text || '').trim();
  const searchValues = Array.isArray(raw.searchValues) ? raw.searchValues : [];
  const searchProfile = createHardwareSearchProfile(raw.text, identity, ...searchValues);
  const identityProfile = createHardwareIdentityProfile(identity);
  return {
    ...raw,
    text: String(raw.text || ''),
    attrs: raw.attrs && typeof raw.attrs === 'object' ? raw.attrs : {},
    _modelIndex: index,
    _searchNormalized: searchProfile.normalized,
    _searchTokens: searchProfile.tokens,
    _identityNormalized: identityProfile.normalized,
    _identityTokens: identityProfile.tokens
  };
}

function getComboboxContainer(containerId) {
  return document.getElementById(containerId);
}

function getDropdown(containerId) {
  return getComboboxContainer(containerId)?.querySelector('.combobox-dropdown') || null;
}

function getPrescoreType(containerId) {
  if (containerId === 'gpuCombobox') return 'gpu';
  if (containerId === 'cpuCoolerCombobox') return 'air';
  if (AIO_COMBOBOX_IDS.includes(containerId)) return 'aio';
  if (containerId === 'moboCombobox') return 'mobo';
  return '';
}

function requestVisibleOptionPrescore(containerId) {
  const hwType = getPrescoreType(containerId);
  if (!hwType) return;
  queueMicrotask(() => batchPreScoreCombobox(containerId, hwType));
}

function createComboboxItem(containerId, model) {
  const el = document.createElement('div');
  el.className = 'combobox-item';
  el.setAttribute('role', 'option');
  el.setAttribute('aria-selected', 'false');
  el.setAttribute('data-model-index', String(model._modelIndex));
  el.setAttribute('data-label', model.text || '');
  el.id = `${containerId}-option-${model._modelIndex}`;
  Object.entries(model.attrs || {}).forEach(([name, value]) => {
    if (value !== null && value !== undefined && value !== '') el.setAttribute(name, String(value));
  });
  el.textContent = model.text || '';
  return el;
}

function getFilteredModels(containerId, query = '') {
  const models = lazyComboboxOptionModels.get(containerId) || [];
  const profile = createHardwareSearchProfile(query);
  if (!profile.tokens.length) return models;
  return models.filter(model => profile.tokens.every(token => model._searchNormalized.includes(token)));
}

function renderComboboxModels(containerId, models, { append = false, query = '', forced = false } = {}) {
  const container = getComboboxContainer(containerId);
  const dropdown = container?.querySelector('.combobox-dropdown') || null;
  if (!container || !dropdown) return false;

  if (!append) dropdown.replaceChildren();
  const frag = document.createDocumentFragment();
  models.forEach(model => frag.appendChild(createComboboxItem(containerId, model)));
  dropdown.appendChild(frag);

  delete container.dataset.sffPrescoreKey;
  const previous = comboboxRenderState.get(containerId) || {};
  comboboxRenderState.set(containerId, {
    query,
    forced,
    filtered: previous.filtered || models,
    renderedCount: append ? (previous.renderedCount || 0) + models.length : models.length
  });
  return true;
}

function renderComboboxQuery(containerId, query = '', { append = false } = {}) {
  const startedAt = runtimeNow();
  const dropdown = getDropdown(containerId);
  if (!dropdown) return false;

  let stateEntry = comboboxRenderState.get(containerId);
  let filtered;
  let start = 0;
  if (append && stateEntry && stateEntry.query === query && !stateEntry.forced) {
    filtered = stateEntry.filtered || [];
    start = stateEntry.renderedCount || 0;
  } else {
    filtered = getFilteredModels(containerId, query);
    stateEntry = { query, filtered, renderedCount: 0, forced: false };
    comboboxRenderState.set(containerId, stateEntry);
  }

  const slice = filtered.slice(start, start + COMBOBOX_PAGE_SIZE);
  if (!append) dropdown.replaceChildren();
  if (slice.length) {
    const frag = document.createDocumentFragment();
    slice.forEach(model => frag.appendChild(createComboboxItem(containerId, model)));
    dropdown.appendChild(frag);
  }

  const renderedCount = start + slice.length;
  comboboxRenderState.set(containerId, { query, filtered, renderedCount, forced: false });
  const container = getComboboxContainer(containerId);
  if (container) delete container.dataset.sffPrescoreKey;

  debugPerformanceMetric(`combobox.render.${containerId}`, startedAt, {
    queryTokens: createHardwareSearchProfile(query).tokens.length,
    matched: filtered.length,
    rendered: renderedCount
  });
  return true;
}

function appendNextComboboxPage(containerId) {
  const current = comboboxRenderState.get(containerId);
  if (!current || current.forced || current.renderedCount >= current.filtered.length) return false;
  const rendered = renderComboboxQuery(containerId, current.query, { append: true });
  if (rendered) requestVisibleOptionPrescore(containerId);
  return rendered;
}

function scoreTargetAgainstModel(target, targetTokens, model) {
  const candidate = model._identityNormalized || model._searchNormalized;
  if (!candidate) return -Infinity;
  if (candidate === target) return 10000;
  if (candidate.includes(target) || target.includes(candidate)) {
    return 5000 - Math.abs(candidate.length - target.length);
  }
  const overlap = (model._identityTokens || model._searchTokens || [])
    .reduce((count, token) => count + (targetTokens.has(token) ? 1 : 0), 0);
  if (overlap >= Math.min(3, targetTokens.size)) {
    return overlap * 100 - Math.abs(candidate.length - target.length);
  }
  return -Infinity;
}

function findBestOptionModel(containerId, part) {
  const wantedCatalogId = String(part?.catalogId || part?.catalog_id || part?.id || '').trim().toLowerCase();
  if (wantedCatalogId) {
    const exactMatches = (lazyComboboxOptionModels.get(containerId) || []).filter(model =>
      String(model?.attrs?.['data-catalog-id'] || '').trim().toLowerCase() === wantedCatalogId
    );
    if (exactMatches.length === 1) return exactMatches[0];

    // A duplicate backend ID is not an exact identity. Never silently choose the first model.
    // Only continue into fuzzy recovery when the caller also supplied a human-readable label.
    if (exactMatches.length > 1 && !(part?.name || part?.model || part?.title || part?.slug)) return null;
  }

  const rawTargets = [
    part?.name || part?.model || part?.title || (typeof part === 'string' ? part : ''),
    String(part?.slug || '').replace(/[-_]+/g, ' ')
  ].filter(Boolean);
  if (!rawTargets.length) return null;

  const targetProfiles = rawTargets
    .map(value => createHardwareIdentityProfile(value))
    .filter(profile => profile.normalized)
    .map(profile => ({ ...profile, tokenSet: new Set(profile.tokens) }));
  if (!targetProfiles.length) return null;

  let best = null;
  let bestScore = -Infinity;
  for (const model of lazyComboboxOptionModels.get(containerId) || []) {
    for (const targetProfile of targetProfiles) {
      const score = scoreTargetAgainstModel(targetProfile.normalized, targetProfile.tokenSet, model);
      if (score > bestScore) {
        bestScore = score;
        best = model;
      }
    }
  }
  return bestScore >= 250 ? best : null;
}

function materializeBestOptionItem(containerId, part) {
  const model = findBestOptionModel(containerId, part);
  if (!model) return null;
  const dropdown = getDropdown(containerId);
  if (!dropdown) return null;
  renderComboboxModels(containerId, [model], { query: '', forced: true });
  requestVisibleOptionPrescore(containerId);
  return dropdown.querySelector(`.combobox-item[data-model-index="${model._modelIndex}"]`);
}

function registerLazyComboboxOptions(containerId, models) {
  const prepared = (Array.isArray(models) ? models : []).map(prepareOptionModel);
  lazyComboboxOptionModels.set(containerId, prepared);
  comboboxRenderState.delete(containerId);

  const container = getComboboxContainer(containerId);
  if (container) {
    delete container.dataset.sffPrescoreKey;
    container._sffEnsureOptions = () => ensureComboboxOptions(containerId);
    container._sffFindBestItem = (part) => materializeBestOptionItem(containerId, part);
    container._sffSearchModels = (query) => getFilteredModels(containerId, query);
  }
  const dropdown = container?.querySelector('.combobox-dropdown') || null;
  if (dropdown) dropdown.replaceChildren();

  const input = container?.querySelector('.combobox-input') || null;
  if (input && document.activeElement === input) {
    queueMicrotask(() => {
      renderComboboxQuery(containerId, input.value || '');
      requestVisibleOptionPrescore(containerId);
    });
  }
}

/**
 * Compatibility alias retained for older callers. "Ensure" now means render a
 * bounded first page, not materialize the entire catalog.
 */
export function ensureComboboxOptions(containerId) {
  const dropdown = getDropdown(containerId);
  if (!dropdown || !lazyComboboxOptionModels.has(containerId)) return false;
  const current = comboboxRenderState.get(containerId);
  if (current && !current.forced && dropdown.querySelector('.combobox-item')) return true;
  return renderComboboxQuery(containerId, '');
}

export const ensureHardwareComboboxOptions = ensureComboboxOptions;

export function initSearchableCombobox(containerId, onSelectCallback, onInputCallback, debouncedEvaluate) {
  const container = getComboboxContainer(containerId);
  if (!container) return;
  const input = container.querySelector('.combobox-input');
  const dropdown = container.querySelector('.combobox-dropdown');
  if (!input || !dropdown) return;

  const isAioCombobox = AIO_COMBOBOX_IDS.includes(containerId);
  let activeItem = null;

  if (!dropdown.id) dropdown.id = `${containerId}-listbox`;
  dropdown.setAttribute('role', 'listbox');
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-controls', dropdown.id);
  input.setAttribute('aria-expanded', 'false');

  const setDropdownOpen = (open) => {
    dropdown.classList.toggle('active', open);
    input.setAttribute('aria-expanded', String(open));
    if (!open) setActiveItem(null);
  };

  const getVisibleItems = () => [...dropdown.querySelectorAll('.combobox-item')];

  const setActiveItem = (item) => {
    if (activeItem) {
      activeItem.classList.remove('active-keyboard-option');
      activeItem.setAttribute('aria-selected', 'false');
    }
    activeItem = item || null;
    if (!activeItem) {
      input.removeAttribute('aria-activedescendant');
      return;
    }
    activeItem.classList.add('active-keyboard-option');
    activeItem.setAttribute('aria-selected', 'true');
    input.setAttribute('aria-activedescendant', activeItem.id);
    activeItem.scrollIntoView({ block: 'nearest' });
  };

  const clearAioIdentity = () => {
    if (isAioCombobox) clearSelectedComponent('aio');
  };

  let clearBtn = container.querySelector('.combobox-clear-btn');
  if (!clearBtn) {
    clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = 'combobox-clear-btn';
    clearBtn.setAttribute('title', 'Clear Search');
    clearBtn.setAttribute('aria-label', 'Clear search input');
    clearBtn.innerHTML = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="sff-clear-icon"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>`;
    container.style.position = 'relative';
    container.appendChild(clearBtn);
  }

  const toggleClearBtn = () => {
    if (clearBtn) clearBtn.style.display = input.value.trim().length > 0 ? 'inline-flex' : 'none';
  };
  toggleClearBtn();

  clearBtn.addEventListener('click', (e) => {
    e.stopPropagation();
    input.value = '';
    if (isAioCombobox) {
      clearAioIdentity();
      syncComboboxInputValues(AIO_COMBOBOX_IDS, '', containerId);
    } else if (containerId === 'gpuCombobox') clearSelectedComponent('gpu');
    else if (containerId === 'cpuCoolerCombobox') clearSelectedComponent('cpu');
    else if (containerId === 'moboCombobox') clearSelectedComponent('mobo');
    else if (containerId === 'psuCombobox') {
      clearSelectedComponent('psu');
      if (state.portableBuildHardware) state.portableBuildHardware.psu = null;
    }
    else if (containerId === 'ramCombobox') clearSelectedComponent('ram');
    toggleClearBtn();
    setDropdownOpen(false);
    comboboxRenderState.delete(containerId);
    state.initialShortcodeUrl = null;
    window._initialShortcodeUrl = null;
    invalidateActiveReport();
    if (onInputCallback) onInputCallback('');
    if (debouncedEvaluate) debouncedEvaluate();
  });

  input.addEventListener('focus', () => {
    setActiveItem(null);
    renderComboboxQuery(containerId, '');
    setDropdownOpen(true);
    requestVisibleOptionPrescore(containerId);
  });

  input.addEventListener('input', () => {
    toggleClearBtn();
    state.initialShortcodeUrl = null;
    window._initialShortcodeUrl = null;
    invalidateActiveReport();
    setDropdownOpen(true);
    setActiveItem(null);

    if (isAioCombobox) {
      clearAioIdentity();
      syncComboboxInputValues(AIO_COMBOBOX_IDS, input.value, containerId);
    } else if (containerId === 'gpuCombobox') clearSelectedComponent('gpu');
    else if (containerId === 'cpuCoolerCombobox') clearSelectedComponent('cpu');
    else if (containerId === 'moboCombobox') clearSelectedComponent('mobo');
    else if (containerId === 'psuCombobox') {
      clearSelectedComponent('psu');
      if (state.portableBuildHardware) state.portableBuildHardware.psu = null;
    }
    else if (containerId === 'ramCombobox') clearSelectedComponent('ram');

    renderComboboxQuery(containerId, input.value);
    requestVisibleOptionPrescore(containerId);

    if (!input.value.trim().length) {
      updateExplorerUiTitle('COMPATIBILITY EXPLORER');
      renderSingleCaseBreadcrumbs('');
    }
    if (onInputCallback) onInputCallback(input.value);
  });

  input.addEventListener('keydown', (e) => {
    if (!dropdown.querySelector('.combobox-item')) renderComboboxQuery(containerId, input.value || '');
    let visibleItems = getVisibleItems();

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!dropdown.classList.contains('active')) setDropdownOpen(true);
      if (!visibleItems.length) return;

      let currentIndex = activeItem ? visibleItems.indexOf(activeItem) : -1;
      if (e.key === 'ArrowDown' && currentIndex === visibleItems.length - 1 && appendNextComboboxPage(containerId)) {
        visibleItems = getVisibleItems();
        currentIndex = activeItem ? visibleItems.indexOf(activeItem) : -1;
      }
      const nextIndex = e.key === 'ArrowDown'
        ? Math.min(currentIndex + 1, visibleItems.length - 1)
        : (currentIndex <= 0 ? visibleItems.length - 1 : currentIndex - 1);
      setActiveItem(visibleItems[nextIndex]);
      return;
    }

    if (e.key === 'Home' && dropdown.classList.contains('active') && visibleItems.length) {
      e.preventDefault();
      setActiveItem(visibleItems[0]);
      return;
    }

    if (e.key === 'End' && dropdown.classList.contains('active') && visibleItems.length) {
      e.preventDefault();
      setActiveItem(visibleItems[visibleItems.length - 1]);
      return;
    }

    if (e.key === 'Enter') {
      e.preventDefault();
      if (activeItem) activeItem.click();
      else {
        setDropdownOpen(false);
        input.blur();
      }
      return;
    }

    if (e.key === 'Escape') {
      e.preventDefault();
      setDropdownOpen(false);
      input.blur();
    }
  });

  dropdown.addEventListener('scroll', () => {
    if (dropdown.scrollTop + dropdown.clientHeight >= dropdown.scrollHeight - 48) {
      appendNextComboboxPage(containerId);
    }
  }, { passive: true });

  dropdown.addEventListener('mousemove', (e) => {
    const item = e.target.closest('.combobox-item');
    if (item) setActiveItem(item);
  });

  dropdown.addEventListener('click', (e) => {
    state.initialShortcodeUrl = null;
    window._initialShortcodeUrl = null;
    invalidateActiveReport();
    const item = e.target.closest('.combobox-item');
    if (!item) return;

    const rawCaseName = item.getAttribute('data-case-name');
    const selectedName = String(item.getAttribute('data-label') || item.textContent || '').trim();
    input.value = rawCaseName || selectedName;
    if (isAioCombobox) syncComboboxInputValues(AIO_COMBOBOX_IDS, input.value, containerId);
    toggleClearBtn();
    setDropdownOpen(false);
    if (onSelectCallback) onSelectCallback(item);
  });

  document.addEventListener('click', (e) => {
    if (!container.contains(e.target)) setDropdownOpen(false);
  });
}

export function populateCaseSearchDropdown(cases) {
  if (!Array.isArray(cases)) return;
  const startedAt = runtimeNow();
  const models = cases
    .filter(c => c && c.name)
    .map(c => ({
      text: formatCaseBrandTitle(c.name),
      identity: c.name,
      searchValues: [c.case_slug, c.slug],
      attrs: { 'data-case-name': c.name, 'data-catalog-id': catalogIdOf(c) }
    }));
  registerLazyComboboxOptions('caseSearchCombobox', models);
  debugPerformanceMetric('combobox.index.cases', startedAt, { options: models.length });
}

export function populateHardwareDropdowns(hw) {
  if (!hw) return;
  const startedAt = runtimeNow();

  state.hardwareGlobal = hw;
  window.hardwareGlobal = hw;

  const findVal = (obj, fields) => {
    for (const f of fields) {
      const cf = f.toLowerCase().replace(/[^a-z0-9]/g, '');
      if (!Object.prototype.hasOwnProperty.call(obj, f)) {
        for (const k in obj) {
          if (!Object.prototype.hasOwnProperty.call(obj, k)) continue;
          const ck = k.toLowerCase().replace(/[^a-z0-9]/g, '');
          if (ck === cf || ck.includes(cf)) return obj[k];
        }
      } else {
        return obj[f];
      }
    }
    return '';
  };

  // Physical dimensions use exact normalized keys only so a generic dimension
  // lookup cannot bind to an unrelated cable, connector or accessory field.
  const findExactVal = (obj, fields) => {
    if (!obj || typeof obj !== 'object') return '';
    const wanted = new Set(fields.map(field => String(field).toLowerCase().replace(/[^a-z0-9]/g, '')));
    for (const [key, value] of Object.entries(obj)) {
      if (wanted.has(String(key).toLowerCase().replace(/[^a-z0-9]/g, ''))) return value;
    }
    return '';
  };

  const rawMobos = [];
  const explicitKeys = [
    'mitx_boards', 'matx_boards', 'mITX Boards', 'mATX Boards',
    'mobos', 'motherboards', 'mobos_mitx', 'mobos_matx',
    'mitx_mobos', 'matx_mobos', 'itx_mobos', 'matx_mobos',
    'itx_boards', 'matx_boards', 'mitx', 'matx', 'itx', 'matx',
    'mitxboards', 'matxboards', 'mitx_boards_data', 'matx_boards_data',
    'mITX_Boards', 'mATX_Boards'
  ];
  const consumedMoboKeys = new Set();
  [...new Set(explicitKeys)].forEach(k => {
    if (!Array.isArray(hw[k])) return;
    consumedMoboKeys.add(k);
    hw[k].forEach(item => { if (item) rawMobos.push({ item, sourceKey: k }); });
  });
  if (hw && typeof hw === 'object') {
    for (const k in hw) {
      if (consumedMoboKeys.has(k) || !Object.prototype.hasOwnProperty.call(hw, k) || !Array.isArray(hw[k])) continue;
      const lk = k.toLowerCase();
      if ((lk.includes('mobo') || lk.includes('board') || lk.includes('mitx') || lk.includes('matx') || lk.includes('itx')) &&
          !lk.includes('case') && !lk.includes('gpu') && !lk.includes('cooler') && !lk.includes('fan') && !lk.includes('psu') && !lk.includes('cpu_') && !lk.includes('riser')) {
        hw[k].forEach(item => { if (item) rawMobos.push({ item, sourceKey: k }); });
      }
    }
  }

  const seenMobos = new Set();
  const moboModels = [];
  rawMobos.forEach(({ item: m, sourceKey }) => {
    const br = String(findVal(m, ['brand', 'mfr', 'manufacturer', 'vendor'])).trim();
    const model = String(findVal(m, ['name', 'model', 'board', 'motherboard', 'title'])).trim();
    const chipset = String(findVal(m, ['chipset'])).trim();
    const socket = String(findVal(m, ['socket'])).trim();
    const ffRaw = String(findVal(m, ['form_factor', 'formfactor', 'size', 'type'])).trim().toUpperCase();
    const height = parseSpecNum(findExactVal(m, ['sizeheightmm', 'sizeheight', 'heightmm', 'height', 'lengthmm', 'length']), 0);
    const width = parseSpecNum(findExactVal(m, ['sizewidthmm', 'sizewidth', 'widthmm', 'width']), 0);
    if (!model) return;

    const skLower = String(sourceKey || '').toLowerCase();
    const modelUpper = model.toUpperCase();
    let ff = 'ITX';
    if (skLower.includes('matx') || ffRaw.includes('MATX') || ffRaw.includes('M-ATX') || modelUpper.includes('MATX') || modelUpper.includes('M-ATX') || height > 180 || width > 180) {
      ff = 'mATX';
    } else if (ffRaw.includes('ATX') && !ffRaw.includes('ITX')) {
      return;
    }

    let displayTitle = br && !model.toLowerCase().startsWith(br.toLowerCase()) ? `${br} ${model}` : model;
    displayTitle = displayTitle.trim().replace(/\s+/g, ' ');
    const uniqueKey = `${displayTitle}_${ff}`.toLowerCase();
    if (seenMobos.has(uniqueKey)) return;
    seenMobos.add(uniqueKey);

    const tagInfo = [];
    if (socket) tagInfo.push(socket);
    if (chipset && !socket.toLowerCase().includes(chipset.toLowerCase())) tagInfo.push(chipset);
    const tagStr = tagInfo.length ? ` (${tagInfo.join(' ')})` : '';
    moboModels.push({
      text: `${displayTitle} [${ff}]${tagStr}`,
      identity: displayTitle,
      searchValues: [br, model, socket, chipset, ff],
      attrs: {
        'data-form-factor': ff,
        'data-brand': br,
        'data-model': model,
        'data-mobo-length': height || '',
        'data-mobo-width': width || '',
        'data-dimension-source': height > 0 && width > 0 ? 'catalog_board_outline' : (height > 0 || width > 0 ? 'catalog_partial' : 'unknown'),
        'data-catalog-id': catalogIdOf(m)
      }
    });
  });
  registerLazyComboboxOptions('moboCombobox', moboModels);

  // Power-supply model search consumes the same `hw.psus` catalog used by the
  // Community Build submission form. Exact identity is optional: the existing
  // PSU form-factor segmented control remains the manual/fallback authority.
  const rawPsus = Array.isArray(hw.psus) ? hw.psus : [];
  const seenPsus = new Set();
  const psuModels = [];
  rawPsus.forEach(psu => {
    if (!psu) return;
    const brand = String(findVal(psu, ['brand', 'vendor', 'manufacturer'])).trim();
    const model = String(findVal(psu, ['model', 'name'])).trim();
    const wattage = parseInt(findVal(psu, ['wattage', 'watts']), 10) || 0;
    if (!model) return;

    let title = brand && !model.toLowerCase().includes(brand.toLowerCase()) ? `${brand} ${model}` : model;
    if (wattage) title += ` (${wattage}W)`;
    title = title.trim().replace(/\s+/g, ' ');

    const normalizedForm = normalizePsuFormFactor(findVal(psu, [
      'form_factor', 'formfactor', 'psu_type', 'psutype', 'type', 'format'
    ]));
    const formFactor = normalizedForm === 'DC' ? 'DC-ATX' : normalizedForm;
    const length = parseSpecNum(findExactVal(psu, ['psulengthmm', 'psulength', 'depthmm', 'depth', 'lengthmm', 'length', 'length_mm', 'psu_length']), 0);
    const width = parseSpecNum(findExactVal(psu, ['widthmm', 'width', 'width_mm']), 0);
    const height = parseSpecNum(findExactVal(psu, ['heightmm', 'height', 'height_mm']), 0);
    const catalogId = catalogIdOf(psu);
    const unique = catalogId || `${title}|${formFactor}|${length}`.toLowerCase();
    if (seenPsus.has(unique)) return;
    seenPsus.add(unique);

    psuModels.push({
      text: title,
      identity: title.replace(/\s*\(\d+\s*W\)\s*$/i, '').trim(),
      searchValues: [brand, model, wattage, formFactor],
      attrs: {
        'data-form-factor': formFactor,
        'data-brand': brand,
        'data-model': model,
        'data-psu-length': length || '',
        'data-psu-width': width || '',
        'data-psu-height': height || '',
        'data-dimension-source': [length, width, height].filter(value => value > 0).length === 3
          ? 'catalog_full'
          : ([length, width, height].some(value => value > 0) ? 'catalog_partial' : 'unknown'),
        'data-psu-wattage': wattage || '',
        'data-catalog-id': catalogId
      }
    });
  });
  registerLazyComboboxOptions('psuCombobox', psuModels);

  const gpuModels = [];
  const gpus = [...(hw.gpus_lt_215 || []), ...(hw.gpus_gt_215 || [])];
  gpus.forEach(g => {
    if (!g) return;
    const br = String(findVal(g, ['brand'])).trim();
    const mo = String(findVal(g, ['model'])).trim();
    const va = String(findVal(g, ['name', 'variant'])).trim();
    if (!mo) return;
    let title = br && mo.toLowerCase().startsWith(br.toLowerCase()) ? mo : `${br} ${mo}`;
    if (va && !title.toLowerCase().includes(va.toLowerCase())) title += ` ${va}`;
    title = title.trim().replace(/\s+/g, ' ');

    const len = parseSpecNum(findVal(g, ['length']), 300);
    const th = parseSpecNum(findVal(g, ['thickness']), 50);
    const he = parseSpecNum(findVal(g, ['width', 'height']), 120);
    let sl = parseFloat(findVal(g, ['bracket', 'slots', 'pcie']));
    if (!sl || Number.isNaN(sl) || sl <= 0) sl = Math.max(1, Math.min(4, Math.ceil(th / 20)));
    gpuModels.push({
      text: `${title} (${len}x${he}x${th}mm)`,
      identity: title,
      searchValues: [br, mo, va],
      attrs: {
        'data-gpu-len': len,
        'data-gpu-thick': th,
        'data-gpu-height': he,
        'data-gpu-slot': sl,
        'data-catalog-id': catalogIdOf(g)
      }
    });
  });
  registerLazyComboboxOptions('gpuCombobox', gpuModels);

  const coolerModels = [];
  const coolers = [...(hw.coolers_lt_70 || []), ...(hw.coolers_gt_70 || []), ...(hw.coolers || [])];
  coolers.forEach(c => {
    if (!c) return;
    const br = String(findVal(c, ['brand'])).trim();
    const cName = String(findVal(c, ['cooler', 'name'])).trim();
    if (!cName) return;
    const displayTitle = (br && !cName.toLowerCase().includes(br.toLowerCase()) ? `${br} ${cName}` : cName).trim().replace(/\s+/g, ' ');
    const len = parseSpecNum(findVal(c, ['length']), 95);
    const width = parseSpecNum(findVal(c, ['width']), 95);
    const height = parseSpecNum(findVal(c, ['height']), 0);
    if (height <= 0) return;
    coolerModels.push({
      text: `${displayTitle} (${len}x${width}x${height}mm)`,
      identity: displayTitle,
      searchValues: [br, cName],
      attrs: { 'data-val': height, 'data-len': len, 'data-width': width, 'data-catalog-id': catalogIdOf(c) }
    });
  });
  registerLazyComboboxOptions('cpuCoolerCombobox', coolerModels);

  let rawAios = [];
  const aioKeys = ['aios', 'aio', 'aio_coolers', 'coolers_aio', 'liquid_coolers', 'aioCoolers', 'AIOs', 'radiators'];
  for (const k of aioKeys) {
    if (Array.isArray(hw[k]) && hw[k].length > 0) { rawAios = hw[k]; break; }
  }
  if (rawAios.length === 0 && hw && typeof hw === 'object') {
    for (const k in hw) {
      if (k.toLowerCase().includes('aio') && Array.isArray(hw[k])) { rawAios = hw[k]; break; }
    }
  }

  const aioModels = rawAios.map(a => {
    if (!a) return null;
    const br = String(findVal(a, ['brand', 'mfr', 'manufacturer'])).trim();
    const model = String(findVal(a, ['model', 'name', 'cooler', 'title'])).trim();
    const radType = String(findVal(a, ['radiatortype', 'type', 'size', 'radsize'])).replace(/mm/gi, '').trim();
    if (!model) return null;
    const aioLen = parseSpecNum(findVal(a, ['radiatorlength', 'length', 'l', 'len']), 274);
    const aioWidth = parseSpecNum(findVal(a, ['radiatorwidth', 'width', 'w']), 120);
    const aioThick = parseSpecNum(findVal(a, ['radiatorthickness', 'thickness', 'h', 'thick', 't']), 27.2);
    const pumpHeight = parseSpecNum(findVal(a, ['cpublockheight', 'blockheight', 'pumpheight', 'height', 'block']), 45);
    const displayTitle = (br && !model.toLowerCase().includes(br.toLowerCase()) ? `${br} ${model}` : model).trim().replace(/\s+/g, ' ');
    return {
      text: `${displayTitle} (${aioLen}x${aioWidth}x${aioThick}mm)`,
      identity: displayTitle,
      searchValues: [br, model, radType],
      attrs: {
        'data-rad-size': radType || (aioLen > 300 ? '360' : (aioLen > 250 ? '240' : '120')),
        'data-rad-len': aioLen,
        'data-rad-width': aioWidth,
        'data-rad-thick': aioThick,
        'data-pump-height': pumpHeight,
        'data-catalog-id': catalogIdOf(a)
      }
    };
  }).filter(Boolean);
  AIO_COMBOBOX_IDS.forEach(id => registerLazyComboboxOptions(id, aioModels));

  // RAM uses the same backend catalog as /3d. Keep the frontend permissive about
  // bucket naming because historic hardware payloads used ram/memory/dimm variants.
  const rawRam = [];
  const seenRamBuckets = new Set();
  for (const [bucket, rows] of Object.entries(hw || {})) {
    if (!Array.isArray(rows)) continue;
    const key = String(bucket).toLowerCase();
    if (!/(^|_|\b)(ram|memory|dimm)(_|\b|$)/.test(key)) continue;
    if (seenRamBuckets.has(bucket)) continue;
    seenRamBuckets.add(bucket);
    rows.forEach(item => { if (item) rawRam.push(item); });
  }

  const seenRam = new Set();
  const ramModels = [];
  rawRam.forEach(ram => {
    const brand = String(findVal(ram, ['brand', 'mfr', 'manufacturer', 'vendor'])).trim();
    const model = String(findVal(ram, ['model', 'name', 'product', 'title'])).trim();
    if (!model) return;
    const memoryType = String(findVal(ram, ['memory_type', 'memorytype', 'type', 'ddr'])).trim().toUpperCase();
    const height = parseSpecNum(findVal(ram, ['height', 'height_mm', 'ramheight', 'moduleheight']), 0);
    const modules = Math.max(1, Math.min(8, parseInt(findVal(ram, ['modules', 'module_count', 'kit_modules', 'sticks', 'count']), 10) || 2));
    const rgbRaw = String(findVal(ram, ['rgb', 'lighting', 'led'])).trim().toLowerCase();
    const rgb = ['1', 'y', 'yes', 'true', 'rgb'].includes(rgbRaw) ? 'RGB' : '';
    const title = (brand && !model.toLowerCase().startsWith(brand.toLowerCase()) ? `${brand} ${model}` : model).trim().replace(/\s+/g, ' ');
    const unique = `${title}|${memoryType}|${height}`.toLowerCase();
    if (seenRam.has(unique)) return;
    seenRam.add(unique);
    const tags = [memoryType, height > 0 ? `${height}mm` : '', rgb].filter(Boolean).join(' · ');
    ramModels.push({
      text: `${title}${tags ? ` (${tags})` : ''}`,
      identity: title,
      searchValues: [brand, model, memoryType, height, rgb],
      attrs: {
        'data-ram-height': height || '',
        'data-ram-modules': modules,
        'data-memory-type': memoryType,
        'data-catalog-id': catalogIdOf(ram)
      }
    });
  });
  registerLazyComboboxOptions('ramCombobox', ramModels);

  debugPerformanceMetric('combobox.index.hardware', startedAt, {
    mobo: moboModels.length,
    psu: psuModels.length,
    gpu: gpuModels.length,
    coolers: coolerModels.length,
    aio: aioModels.length,
    ram: ramModels.length
  });
}
